import { Toast } from "antd-mobile";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomTable, Filter, FilterTag } from "../../../../../components";
import { getLastSixWeeks } from "../../../../../plugins/monthList";
import { moneyFormat } from "../../../../../plugins/numberFormat";
import { getCurrency } from "../../../../../plugins/utils";
import { filterKeyReceivedData } from "../../enumerations/filterKeyEnum";
import { filterSetupReceivedData } from "../../enumerations/filterSetupEnum";
import receivedStatus from "../../enumerations/receivedStatus";
import { receivedStatusEnum } from "../../enumerations/transactionStatusEnum";
import { useGetReceivedList, useReceiveReward } from "../../hooks/trade.hooks";
import styles from "./style.module.scss";
import "./style.scss";

const CURRENCY = getCurrency();
const LAST_SIX_WEEKS = getLastSixWeeks();

const Received = () => {
  const { t } = useTranslation();
  const [months, setMonths] = useState([]);
  const [weeks, setWeeks] = useState(LAST_SIX_WEEKS);
  const [filterDefaultValue, setFilterDefaultValue] = useState({
    [filterKeyReceivedData.PERIOD_WEEK]: weeks[0]?.value,
    [filterKeyReceivedData.STATUS]: null,
  });
  const [filterValue, setFilterValue] = useState({
    [filterKeyReceivedData.PERIOD_WEEK]: weeks[0]?.value,
    [filterKeyReceivedData.STATUS]: null,
  });

  const { mutate: receiveReward } = useReceiveReward();

  const handleReceiveReward = async (id) => {
    await receiveReward(
      { id: id },
      {
        onSuccess: () =>
          Toast.show({
            icon: "success",
            content: t("normal.isSent"),
          }),
      },
      {
        onError: (error) => {
          Toast.show({
            icon: "fail",
            content: error,
          });
        },
      },
    );
  };

  const renderFunctionButton = (data) => {
    if (data.status === receivedStatus.PENDING) {
      return (
        <div className={styles.buttonBlue} onClick={() => handleReceiveReward(data.id)}>
          {t("normal.receive")}
        </div>
      );
    }
    return <div className={styles.buttonDisabled}>{t("normal.receive")}</div>;
  };
  const [localData, setLocalData] = useState([]);

  const {
    data: receivedData,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isError,
  } = useGetReceivedList({
    params: {
      ...filterValue,
      period_date: filterValue[filterKeyReceivedData.PERIOD_WEEK]?.split("~")[0],
    },
    options: {
      enabled: filterValue[filterKeyReceivedData.PERIOD_WEEK] !== null,
      getNextPageParam: (lastPage, pages) => {
        if (pages.length < lastPage.last_page) {
          return pages.length + 1;
        } else {
          return undefined;
        }
      },
    },
  });

  const loadMore = () => {
    if (isFetchingNextPage === false && localData.length > 0) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (receivedData) {
      const page_length = receivedData.pages.length;
      if (page_length <= 1) {
        setLocalData([...receivedData.pages[0].data]);
      } else {
        setLocalData((prevState) => {
          return [...prevState, ...receivedData.pages[page_length - 1].data];
        });
      }
    }
  }, [receivedData]);

  const onHandleSearch = () => {};

  const renderTransType = (record, transType) => {
    const dateFormat = "MM/DD/YY";
    const formatDate = (date) => {
      if (!date) return;
      return dayjs(date).format(dateFormat);
    };
    switch (transType) {
      case "APRB":
        return `${t(`tradeRecord.rewardType.${transType}`)} (${record.title})`;
      case "APBO":
        return `${t(`tradeRecord.rewardType.${transType}`)} (${record.usre_threshold}/${
          record.bonus_amount
        })`;
      case "APEBO":
        return `${t(`tradeRecord.rewardType.${transType}`)} (${t(
          `tradeRecord.received.extraBonus.level${record.level}`,
        )})`;
      case "ALFB":
      case "ABAB":
      case "ADB":
        return `${t(`tradeRecord.rewardType.${transType}`)} (${record.remark})`;
      default:
        return `${t(`tradeRecord.rewardType.${transType}`)} (${formatDate(
          record.start_date,
        )} - ${formatDate(record.end_date)})`;
    }
  };

  const columns = [
    {
      title: t("tradeRecord.table.serialNumber"),
      dataIndex: "index",
      width: 60,
      align: "center",
      render: (text, record, index) => <>{index + 1}</>,
    },
    {
      title: t("normal.time"),
      width: 150,
      align: "center",
      dataIndex: "created_at",
    },
    {
      title: t("tradeRecord.table.receiptNo"),
      width: 200,
      align: "center",
      dataIndex: "trans_id",
    },
    {
      title: t("comission.table.promoType"),
      width: 150,
      dataIndex: "trans_type",
      align: "center",
      render: (text, record, index) => {
        return (
          <div className={styles.typeColumnContainer}>
            {renderTransType(record, record.trans_type)}
          </div>
        );
      },
    },
    {
      title: `${t("normal.amount")} (${t(`global.currency.${CURRENCY}`)})`,
      width: 140,
      dataIndex: "trade_amount",
      align: "center",
      render: (text, record, index) => <span style={text < 0 ? { color: "red" } : {}}>{moneyFormat(text)}</span>,
    },
    {
      title: t("tradeRecord.table.status"),
      dataIndex: "status",
      width: 70,
      align: "center",
      render: (text, record, index) => {
        const _status = receivedStatusEnum.find((stat) => stat.value === parseInt(text));
        if (!_status) return "-";
        return (
          <div className={`${_status.key}`}>
            {_status.is_translation ? t(`${_status.label}`) : _status.label}
          </div>
        );
      },
    },
    {
      title: t("normal.operation"),
      dataIndex: "operation",
      fixed: "right",
      width: 60,
      align: "center",
      render: (text, record, index) => <>{renderFunctionButton(record)}</>,
    },
  ];

  return (
    <div className={`${styles.container} received-container`}>
      <Filter
        filterSetup={filterSetupReceivedData}
        filterValue={filterValue}
        filterDefaultValue={filterDefaultValue}
        setFilterValue={setFilterValue}
        //If data loaded from API, add api data here
        apiValue={{
          [filterKeyReceivedData.PERIOD_WEEK]: {
            value_enum: weeks,
          },
        }}
        onReset={() => {
          //if need some aditional reset logic in the page
          console.debug("RESET FILTER");
        }}
        onSubmitSearch={onHandleSearch}
      />
      <FilterTag
        filterSetup={filterSetupReceivedData}
        filterValue={filterValue}
        filterDefaultValue={filterDefaultValue}
        setFilterValue={setFilterValue}
        apiValue={{
          [filterKeyReceivedData.PERIOD_WEEK]: {
            value_enum: weeks,
          },
        }}
        refreshSearch={onHandleSearch}
      />
      {isLoading ? (
        ""
      ) : isError ? (
        ""
      ) : (
        <CustomTable
          columns={columns}
          dataSource={localData}
          hasMore={hasNextPage}
          loadMore={loadMore}
          showInifineScroll={true}
        />
      )}
    </div>
  );
};

export default Received;
